import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { ExportLanguage } from '@app/core/components/export-language-dialog';
import {
	ApiEndpoint,
	ApiResponse,
	AssessmentCatalog,
	AssessmentStatusInfo,
	BillingType,
	Client,
	ClientConfig,
	Conversation,
	ConversationFile,
	CreateMessage,
	CreateNewChatPayload,
	EditableCatalog,
	MessageHistory,
	PaymentHistory,
	QueryPagination,
	SignNotePayload,
} from '@app/core/types';
import { environment } from '@app/env';
import * as api from '@stripe/stripe-js/dist/api/index';

@Injectable({ providedIn: 'root' })
export class ApiService {
	#http = inject(HttpClient);

	lastChats() {
		return this.#http.get<ApiResponse<Conversation[]>>(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.Conversation}`,
			{
				params: {
					take: 5,
				},
			}
		);
	}

	getClients() {
		return this.#http.get<ApiResponse<Client[]>>(
			`${environment.MAIN_API_URL}${ApiEndpoint.AllClients}`,
			{
				params: {
					withRelations: true,
				},
			}
		);
	}

	createChat(data: CreateNewChatPayload) {
		return this.#http.post<ApiResponse<Conversation>>(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.Conversation}`,
			data
		);
	}

	/**
	 * Catálogo estructurado del assessment del cliente. Pobla los selects de
	 * conductas ocurridas y programas trabajados (S4). El backend responde 404
	 * cuando el cliente no tiene assessment/catálogo — el llamador lo trata como
	 * "sin catálogo" (estado vacío), no como error.
	 */
	getAssessmentCatalog(clientId: string) {
		return this.#http.get<AssessmentCatalog>(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.AssessmentCatalog}/${clientId}`
		);
	}

	/**
	 * Estado de catálogo de VARIOS pacientes en una sola petición, para poder
	 * distinguirlos en la lista ANTES de elegir uno (REQ-008).
	 *
	 * El backend descarta en silencio los ids que no son de quien pregunta, así
	 * que la respuesta puede traer menos entradas de las que se pidieron.
	 */
	getAssessmentStatus(clientIds: string[]) {
		return this.#http.get<Record<string, AssessmentStatusInfo>>(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.AssessmentStatus}`,
			{ params: { clientIds } }
		);
	}

	/** Config por cliente (pagador/estado + servicios CPT autorizados). */
	getClientConfig(clientId: string) {
		return this.#http.get<ClientConfig | null>(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.ClientConfig}/${clientId}`
		);
	}

	/**
	 * Guarda una edición del catálogo (permanece en `draft`).
	 * `PUT /api/assessment/catalog/:clientId`.
	 */
	updateAssessmentCatalog(clientId: string, catalog: EditableCatalog) {
		return this.#http.put<AssessmentCatalog>(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.AssessmentCatalog}/${clientId}`,
			catalog
		);
	}

	/**
	 * Confirma el catálogo (`draft → confirmed`): pasa a ser fuente de verdad de
	 * los selects. `POST /api/assessment/catalog/:clientId/confirm`.
	 */
	confirmAssessmentCatalog(clientId: string) {
		return this.#http.post<AssessmentCatalog>(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.AssessmentCatalog}/${clientId}/confirm`,
			{}
		);
	}

	getConversation(conversationId: string) {
		return this.#http.get<ApiResponse<Conversation>>(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.Conversation}/${conversationId}`
		);
	}

	createMessage(conversationId: string, data: CreateMessage) {
		const endpoint = `${environment.CHATGPT_API_URL}${ApiEndpoint.Conversation}/${conversationId}${ApiEndpoint.Message}`;
		return this.#http.post<ApiResponse<MessageHistory>>(endpoint, data);
	}

	getPaginatedChats(params: QueryPagination) {
		return this.#http.get<ApiResponse<Conversation[]>>(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.Conversation}`,
			{
				params: { ...params },
			}
		);
	}

	removeConversation(id: string) {
		return this.#http.delete<ApiResponse<null>>(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.Conversation}/${id}`
		);
	}

	/**
	 * Genera el DOCX de la nota firmada en el idioma elegido (H6).
	 *
	 * `language` viaja en el cuerpo. El backend lo trata como opcional y sin él
	 * exporta en inglés, que es lo que hacía siempre: así un cliente cacheado que
	 * todavía mande `{}` no se queda sin export.
	 */
	exportConversation(conversationId: string, language: ExportLanguage) {
		return this.#http.post<ApiResponse<ConversationFile>>(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.Conversation}/${conversationId}${ApiEndpoint.Export}`,
			{ language }
		);
	}

	/**
	 * Descarga un DOCX exportado.
	 *
	 * Antes el frontend enlazaba directamente al CDN (`file.path | cdnPath`), lo
	 * que solo funcionaba porque el objeto se subía con ACL pública: una nota
	 * clínica firmada descargable por cualquiera que conociera la URL. Ahora el
	 * objeto es privado y los bytes vienen del backend, que comprueba el dueño.
	 *
	 * Es una petición autenticada, no una navegación: un `<a href>` no lleva la
	 * cabecera `Authorization` y habría dado 401.
	 */
	downloadConversationFile(conversationId: string, fileId: string) {
		return this.#http.get(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.Conversation}/${conversationId}${ApiEndpoint.File}/${fileId}`,
			{ responseType: 'blob' }
		);
	}

	/**
	 * Firma la nota (WS4 paso 6 / S9): `POST /conversation/:id/sign`. Envía el texto
	 * FINAL revisado (en/es) + la confirmación de la atestación. El backend valida
	 * que el firmante sea el rendering provider (rol de la sesión), añade el bloque
	 * de firma, deja la nota inmutable (`completed`) y habilita el export.
	 */
	signNote(conversationId: string, payload: SignNotePayload) {
		return this.#http.post<
			ApiResponse<{ conversation: Conversation; note: MessageHistory }>
		>(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.Conversation}/${conversationId}${ApiEndpoint.Sign}`,
			payload
		);
	}

	createPaymentIntent(payload: { amount: number; billing: BillingType }) {
		return this.#http.post<ApiResponse<{ client_secret: string }>>(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.PaymentIntent}`,
			payload
		);
	}

	savePayment(payload: {
		amount: number;
		stripeResponse: api.PaymentIntent;
		billing: BillingType;
	}) {
		return this.#http.post<ApiResponse<PaymentHistory>>(
			`${environment.CHATGPT_API_URL}${ApiEndpoint.SavePayment}`,
			payload
		);
	}
}
